import { MemberNameType } from "@msidentity/SISU/model/user";
import { replaceTokens } from "@msidentity/SISU/utilities/strings-helper";
import {
  usernameRecoveryGenericStringsFabric,
  usernameRecoveryResultsViewStringsFabric,
  usernameRecoverySelectAliasStringsFabric,
} from "../fabric/username-recovery-strings-fabric";
import {
  type UsernameRecoveryProofState,
  useUsernameRecoveryContext,
} from "../username-recovery-context";
import { UsernameRecoveryResultAliasType } from "../username-recovery-types";

type UseSelectAliasStringsProps = {
  isGenericServiceError: boolean;
  filteredAccountsForProofResult: UsernameRecoveryProofState;
  isMultipleAliases: boolean;
};

/**
 * Custom hook for select alias strings.
 * @param param0 - props for select alias strings
 * @param param0.isGenericServiceError - flag to indicate if there is a generic service error
 * @param param0.filteredAccountsForProofResult - filtered accounts for proof result
 * @param param0.isMultipleAliases - flag to indicate if there are multiple aliases
 * @returns strings for select alias
 */
export const useSelectAliasStrings = function useSelectAliasStrings({
  isGenericServiceError,
  filteredAccountsForProofResult,
  isMultipleAliases,
}: UseSelectAliasStringsProps) {
  const {
    viewState: { usernameDisplay, usernameType },
  } = useUsernameRecoveryContext();
  const noAliases = !filteredAccountsForProofResult.aliases.length;
  let usernameRecoveryTitle = "";
  let usernameRecoveryDescription = "";

  if (isGenericServiceError) {
    usernameRecoveryTitle = usernameRecoveryGenericStringsFabric.temporaryProblem;
    usernameRecoveryDescription = usernameRecoveryGenericStringsFabric.genericServiceError;
  } else if (noAliases) {
    usernameRecoveryTitle = usernameRecoveryGenericStringsFabric.title;
    usernameRecoveryDescription =
      usernameRecoveryResultsViewStringsFabric.noAssociatedAccountsDescription;
  } else if (isMultipleAliases) {
    usernameRecoveryTitle = usernameRecoverySelectAliasStringsFabric.multipleAliasTitle;
    usernameRecoveryDescription =
      usernameType === MemberNameType.Phone
        ? replaceTokens(
            usernameRecoverySelectAliasStringsFabric.multipleAliasPhoneDesc,
            usernameDisplay,
          )
        : replaceTokens(
            usernameRecoverySelectAliasStringsFabric.multipleAliasEmailDesc,
            usernameDisplay,
          );
  } else {
    usernameRecoveryTitle = usernameRecoverySelectAliasStringsFabric.singleAliasTitle;
    const associatedAlias = filteredAccountsForProofResult.aliases[0];

    if (associatedAlias.isUnmasked) {
      usernameRecoveryDescription = replaceTokens(
        usernameRecoverySelectAliasStringsFabric.unmaskedAliasDesc,
        associatedAlias.name,
      );
    } else if (associatedAlias.type === UsernameRecoveryResultAliasType.Phone) {
      usernameRecoveryDescription = replaceTokens(
        usernameRecoverySelectAliasStringsFabric.singlePhoneAliasDesc,
        "4",
        associatedAlias.name,
      );
    } else {
      usernameRecoveryDescription = replaceTokens(
        usernameRecoverySelectAliasStringsFabric.maskedAliasDesc,
        associatedAlias.name,
      );
    }
  }

  const showTryAgain = isGenericServiceError || noAliases;
  const primaryButtonText = showTryAgain
    ? usernameRecoveryGenericStringsFabric.tryAgain
    : usernameRecoveryGenericStringsFabric.signin;

  return { usernameRecoveryTitle, usernameRecoveryDescription, primaryButtonText };
};
